import _ from 'lodash'
import ApiClient from '../../backend/ApiClient'
import { IDataFrame } from '../../definitions/DataAnalytics'
import Helper from '../../helpers/Helper'
import TimeHelper from '../../helpers/TimeHelper'
import { useAppSelector } from '../../store/Hooks'
import { BarChart, HorizontalBarChart } from '../charts'
import { GetBarChartDefaultOptions } from '../charts/BarChart'
import SmartCard from '../controls/cards/SmartCard'
import ChartCard from '../controls/data_visualization/ChartCard'
import KPICard from '../controls/data_visualization/KPICard'
import StaleObserver from '../controls/stale/StaleObserver'
import useQuery, { buildQueryKey } from '../hooks/useQuery'
import Tile from '../layout/Tile'

interface P {
    contractId: number
}

export default function ProgramAnalytics({ contractId }: P) {
    const time = useAppSelector(s => s.app.timeSettings ?? TimeHelper.timeSettings())
    const { status, response } = useQuery({
        queryKey: buildQueryKey('program-detail', contractId, TimeHelper.stringify(time)),
        queryFn: () => ApiClient.getProgramDetails(contractId, time),
        cache: true
    })

    const isLoading = status === 'loading'

    const programRevenuePMPM = response ? _.last(response.totalRevenue?.enrolled?.points)?.y : 0
    const programTCOCPMPM = response ? _.last(response.totalTCOC?.enrolled?.points)?.y : 0
    const programMemberRetention = response ? _.last(response.retention?.enrolled?.points)?.y : 0
    const programHospitalizationVisits = response ? _.last(response.hospitalization?.enrolled?.points)?.y : 0
    const programEmergencyRoomVisits = response ? _.last(response.emergency?.enrolled?.points)?.y : 0
    const programOverallPatientSatisfaction = 1

    const nonProgramRevenuePMPM = response ? _.last(response.totalRevenue?.unenrolled?.points)?.y : 0
    const nonProgramTCOCPMPM = response ? _.last(response.totalTCOC?.unenrolled?.points)?.y : 0
    const nonProgramMemberRetention = response ? _.last(response.retention?.unenrolled?.points)?.y : 0
    const nonProgramHospitalizationVisits = response ? _.last(response.hospitalization?.unenrolled?.points)?.y : 0
    const nonProgramEmergencyRoomVisits = response ? _.last(response.emergency?.unenrolled?.points)?.y : 0
    const nonProgramOverallPatientSatisfaction = 2

    const utilizationRate: IDataFrame = response ? response.utilizationRate : { points: [] }
    const roi: IDataFrame = response ? response.roi : { points: [] }
    const saving: IDataFrame = response ? response.savings : { points: [] }
    const cost: IDataFrame = response ? response.costPerMember : { points: [] }

    const firstColor = '#005dff'
    const secondColor = '#00a2ff'

    return (
        <Tile.Container>
            <Tile.Row flex={0} spacing={2}>
                <SmartCard flex={1}>
                    <KPICard
                        data={Helper.dataframeToKPICard(response?.eligible)}
                        isLoading={isLoading}
                        label='Total Members'
                        tooltipKey='total_members'
                    />
                </SmartCard>
                <SmartCard flex={1}>
                    <KPICard
                        data={Helper.dataframeToKPICard(response?.partecipationRate)}
                        isLoading={isLoading}
                        unit='%'
                        label='Participation Rate'
                        tooltipKey='participation_rate'
                    />
                </SmartCard>
                <SmartCard flex={1}>
                    <KPICard
                        data={Helper.dataframeToKPICard(response?.tcocPMPM)}
                        isLoading={isLoading}
                        unit='$'
                        inverse
                        label='Total Program/Benefit Cost'
                        tooltipKey='total_program_benefit_cost'
                    />
                </SmartCard>
                <SmartCard flex={1}>
                    <KPICard
                        data={Helper.dataframeToKPICard(response?.costPerMember)}
                        isLoading={isLoading}
                        unit='$'
                        inverse
                        label='AVG. Cost per Member'
                        tooltipKey='avg_cost_per_member'
                    />
                </SmartCard>
            </Tile.Row>
            <Tile.Row flex={1} spacing={2} style={{ minHeight: 250 }}>
                <SmartCard flex={1}>
                    <ChartCard
                        title='Revenue'
                        chart={(
                            <HorizontalBarChart
                                isLoading={isLoading}
                                data={{
                                    datasets: [{ data: [programRevenuePMPM, nonProgramRevenuePMPM], backgroundColor: [firstColor, secondColor] }],
                                    labels: ['program', 'non-program']
                                }}
                            />
                        )}
                    />
                </SmartCard>
                <SmartCard flex={1}>
                    <ChartCard
                        title='TCOC'
                        chart={(
                            <HorizontalBarChart
                                isLoading={isLoading}
                                data={{
                                    datasets: [{ data: [programTCOCPMPM, nonProgramTCOCPMPM], backgroundColor: [firstColor, secondColor] }],
                                    labels: ['program', 'non-program']
                                }}
                            />
                        )}
                    />
                </SmartCard>
                <SmartCard flex={1}>
                    <ChartCard
                        title='Member Retention'
                        chart={(
                            <HorizontalBarChart
                                isLoading={isLoading}
                                data={{
                                    datasets: [{ data: [programMemberRetention, nonProgramMemberRetention], backgroundColor: [firstColor, secondColor] }],
                                    labels: ['program', 'non-program']
                                }}
                            />
                        )}
                    />
                </SmartCard>
            </Tile.Row>
            <Tile.Row flex={1} spacing={2} style={{ minHeight: 250 }}>
                <SmartCard flex={1}>
                    <ChartCard
                        title='Hospitalization Visits'
                        chart={(
                            <HorizontalBarChart
                                isLoading={isLoading}
                                data={{
                                    datasets: [{ data: [programHospitalizationVisits, nonProgramHospitalizationVisits], backgroundColor: [firstColor, secondColor] }],
                                    labels: ['program', 'non-program']
                                }}
                            />
                        )}
                    />
                </SmartCard>
                <SmartCard flex={1}>
                    <ChartCard
                        title='Emergency Room Visits'
                        chart={(
                            <HorizontalBarChart
                                isLoading={isLoading}
                                data={{
                                    datasets: [{ data: [programEmergencyRoomVisits, nonProgramEmergencyRoomVisits], backgroundColor: [firstColor, secondColor] }],
                                    labels: ['program', 'non-program']
                                }}
                            />
                        )}
                    />
                </SmartCard>
                <SmartCard flex={1}>
                    <ChartCard
                        title='Overall Patient Satisfaction'
                        chart={(
                            <HorizontalBarChart
                                isLoading={isLoading}
                                data={{
                                    datasets: [{ data: [programOverallPatientSatisfaction, nonProgramOverallPatientSatisfaction], backgroundColor: [firstColor, secondColor] }],
                                    labels: ['program', 'non-program']
                                }}
                            />
                        )}
                    />
                </SmartCard>
            </Tile.Row>
            <Tile.Row flex={1} spacing={2} style={{ minHeight: 350 }}>
                <SmartCard flex={1}>
                    <ChartCard
                        title='Utilization Rate'
                        chart={(
                            <BarChart
                                isLoading={isLoading}
                                options={{ ...GetBarChartDefaultOptions(), plugins: { ...GetBarChartDefaultOptions().plugins, legend: { display: false } } }}
                                data={{
                                    datasets: [{ data: utilizationRate?.points?.map(x => x?.y) }],
                                    labels: utilizationRate?.points?.map(x => x?.x)
                                }}
                            />
                        )}
                    />
                </SmartCard>
                <SmartCard flex={1}>
                    <ChartCard
                        title='ROI'
                        chart={(
                            <BarChart
                                isLoading={isLoading}
                                data={{
                                    datasets: [
                                        { data: saving?.points?.map(x => x.y), label: 'Saving', order: 1, borderColor: firstColor, backgroundColor: firstColor },
                                        { data: cost?.points?.map(x => x.y), label: 'Cost', order: 1, borderColor: secondColor, backgroundColor: secondColor },
                                        { data: roi?.points?.map(x => x.y), label: 'ROI', type: 'line' as any, order: 0, yAxisID: 'y2' }
                                    ],
                                    labels: roi?.points?.map(x => x.x)
                                }}
                            />
                        )}
                    />
                </SmartCard>
            </Tile.Row>
            <StaleObserver status={status} />
        </Tile.Container>
    )
}
